import Button from 'components/Button';
import Error from 'components/form/Error';
import Input from 'components/form/Inputs/Input';
import Select from 'components/form/Inputs/Select';
import Success from 'components/form/Success';
import Warning from 'components/form/Warning';
import { initialState, State } from 'components/Interview/SuggestForm';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import ActionModal from 'components/Modal/ActionModal';
import { PageBack, PageHeadContainer, PageTitle } from 'components/PageHead';
import Space from 'components/Space';
import INTERVIEW_TYPE, { interviewTypes } from 'enums/InterviewTypes';
import { DateTime } from 'luxon';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import Interview from 'types/Interview';
import manageTextError from 'utils/manageTextError';
import privateAPI from 'utils/privateAxiosInstance';

type ContextType = {
  interview: Interview | undefined;
};
const InterviewEditContext = React.createContext({} as ContextType);
const { Provider } = InterviewEditContext;

const Edit: FC = ({ children }) => {
  const { id } = useParams();
  const { data: interview, error } = useSWR<Interview>(
    `/interview/details/mentor/${id}`,
  );

  const value = useMemo(
    () => ({
      interview,
    }),
    [interview],
  );
  return !error ? (
    interview ? (
      <Provider value={value}>{children}</Provider>
    ) : (
      <LoaderContainer>
        <DefaultLoader size={1.5} />
      </LoaderContainer>
    )
  ) : (
    <>
      <Head />
      <Error>Une erreur est survenue.</Error>
    </>
  );
};

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.5rem;
`;

const Head: FC = () => {
  const { interview } = useContext(InterviewEditContext);
  const [searchParams] = useSearchParams();
  const backTo: string | undefined = useMemo(() => {
    return (
      searchParams.get('from') ||
      `/mentor/mentorship/management/interviews/${interview?.id}`
    );
  }, [searchParams, interview?.id]);

  return (
    <PageHeadContainer>
      <PageTitle>
        <div>
          <Title>
            <PageBack href={backTo} />
            <h1>Modifier les détails de l'entretien</h1>
          </Title>
        </div>
      </PageTitle>
    </PageHeadContainer>
  );
};

const Form = styled.form`
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  row-gap: 1rem;
  h1 {
    color: black;
    margin-bottom: 0.5rem;
  }
  button {
    margin-left: auto;
    margin-right: 0;
  }
`;
const Description = styled.div`
  margin-top: 5rem;
  margin-bottom: 3rem;
  align-self: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 40rem;
  row-gap: 1rem;
  color: var(--gray-500);
`;

const EditForm: FC = () => {
  const { interview } = useContext(InterviewEditContext);
  const [state, setState] = useState<State>(initialState);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (interview) {
      const date = DateTime.fromISO(interview.date || '', {
        zone: 'Europe/Paris',
      });
      setState({
        type: interview.type || '',
        date: date.toFormat('yyyy-MM-dd'),
        time: date.toFormat('HH:mm'),
        mentorshipId: -1,
      });
    }
  }, [interview]);

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement & HTMLSelectElement
  > = (e) => {
    if (e.target.name === 'mentorshipId') {
      setState({
        ...state,
        [e.target.name]: parseInt(e.target.value),
      });
    } else {
      setState({
        ...state,
        [e.target.name]: e.target.value,
      });
    }
    setSuccess('');
    setError('');
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    const date = DateTime.fromFormat(
      `${state.date} ${state.time}`,
      'yyyy-MM-dd HH:mm',
      { zone: 'Europe/Paris' },
    );
    privateAPI
      .post(`/artificial-interview/${interview?.id}`, {
        date,
        type: state.type,
      })
      .then(() => {
        setSuccess('Enregistré.');
      })
      .catch((err) => {
        manageTextError(err, setError);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <>
      <Form onSubmit={handleSubmit}>
        {success && <Success>{success}</Success>}
        {error && <Error>{error}</Error>}
        <Input
          type="date"
          label="Date de l'entretien"
          value={state.date}
          name="date"
          onChange={handleChange}
          required
          placeholder="aaaa-mm-jj (ex: 2022-05-19)"
        />
        <Input
          type="time"
          label="Heure (*)"
          value={state.time}
          name="time"
          onChange={handleChange}
          required
          placeholder="hh:mm"
        />
        <Select
          value={state.type}
          onChange={handleChange}
          name="type"
          label="Type d'entretien"
          required
        >
          <option value="">-- Sélectionner un type d'entretien --</option>
          {interviewTypes.map((type, idx) => {
            return (
              <option value={type} key={idx}>
                {INTERVIEW_TYPE[type]}
              </option>
            );
          })}
        </Select>
        <Button color="primary" loading={loading}>
          Enregistrer
        </Button>
      </Form>
      <Description>
        <p>
          Puisque vous avez ajouté cet entretien à votre historique
          manuellement, vous pouvez en modifier les détails.
        </p>
        <p>
          Pour rappel, les heures sélectionnées et indiquées sur l'application
          sont calibrées sur le fuseau horaire de Paris (UTC+01:00). Si vous
          vous trouvez sur un fuseau horaire différent, veillez à convertir
          l'heure de votre entretien pour qu'elle corresponde au fuseau horaire
          de Paris.
        </p>
        <Warning>
          Certains navigateurs, comme Safari ou Internet Explorer, proposent des
          fonctionnalités limitées, ce qui peut compliquer la prise de
          rendez-vous. Si vous rencontrez des problèmes (notamment pour
          sélectionner la date de l'entretien), nous vous conseillons de
          télécharger un nouveau navigateur, tel que Chrome ou Firefox.
        </Warning>
      </Description>
    </>
  );
};

const DeleteButtonContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 40rem;
  align-self: center;
  margin-bottom: 5rem;
`;
const Delete: FC = () => {
  const { interview } = useContext(InterviewEditContext);

  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleDelete = () => {
    setLoading(true);
    setError('');
    privateAPI
      .delete(`/artificial-interview/${interview?.id}`)
      .then(() => {
        setOpen(false);
        mutate('/interview/mentor');
        navigate('/mentor/mentorship/management/interviews/');
      })
      .catch(() => {
        setError('Une erreur est survenue.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <ActionModal
        open={open}
        close={() => setOpen(false)}
        title="Supprimer l'entretien"
        apply={handleDelete}
        applyText="Supprimer définitivement"
        loading={loading}
        color="error"
      >
        {error && (
          <>
            <Error>Une erreur est survenue.</Error>
            <Space height={0.5} />
          </>
        )}
        <p>Vous êtes sur le point de supprimer un entretien.</p>
        <p>
          Cette action est irreversible et entraînera la suppression des
          comptes-rendus relatifs à l'entretien.
        </p>
        <p>Souhaitez-vous supprimer supprimer cet entretien définitivement ?</p>
      </ActionModal>
      <DeleteButtonContainer>
        <Button variant="outlined" color="error" onClick={() => setOpen(true)}>
          Supprimer l'entretien
        </Button>
      </DeleteButtonContainer>
    </>
  );
};

const Usage: FC = () => {
  return (
    <Edit>
      <Head />
      <EditForm />
      <Delete />
    </Edit>
  );
};

export default Usage;
